import {
  Badge,
  Button,
  Card,
  Divider,
  FormControl,
  IconButton,
  Icons,
  Sheet,
  Stack,
  Table,
  Textarea,
  Typography,
} from '@healthinal/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  useUpdateAnamnesisSecondaryDiagnoses,
  useUpdateAnamnesisSecondaryDiagnosesNote,
} from '../../../../../api/created/anamnesis.ts';
import type { GetAnamnesesByPatient200, SecondaryDiagnosisDto } from '../../../../../api/generated/model';
import { SecondaryDiagnosisDtoDiagnosisType } from '../../../../../api/generated/model';
import { getGetAnamnesesByPatientQueryKey } from '../../../../../api/generated/patient-controller/patient-controller.ts';
import { TruncateText } from '../../../../../components/TruncateText.tsx';
import { getSecondaryDiagnosisTypeName } from '../../../../../utils/anamnesis.ts';
import { formatDateWithGermanMonthOrText } from '../../../../../utils/date.ts';
import { AddSecondaryDiagnoseDialog } from './AddSecondaryDiagnoseDialog.tsx';
import { EditAnamnesisItemsDialog } from './EditAnamnesisItemsDialog.tsx';

export function SecondaryDiagnosesCard({
  diagnoses,
  secondaryDiagnosesNote,
  anamnesisId,
  patientId,
}: {
  diagnoses: SecondaryDiagnosisDto[];
  secondaryDiagnosesNote?: string | null;
  anamnesisId: string;
  patientId: string;
}) {
  const queryClient = useQueryClient();
  const [isAddSecondaryDiagnoseDialogOpen, setIsAddSecondaryDiagnoseDialogOpen] = useState(false);
  const [isEditSecondaryDiagnosesDialogOpen, setIsEditSecondaryDiagnosesDialogOpen] = useState(false);
  const [note, setNote] = useState(secondaryDiagnosesNote ?? '');
  const shouldScrollDiagnoses = diagnoses.length > 10;
  const isNoteDirty = note !== (secondaryDiagnosesNote ?? '');

  useEffect(() => {
    setNote(secondaryDiagnosesNote ?? '');
  }, [secondaryDiagnosesNote]);

  const updateSecondaryDiagnosesNoteMutation = useUpdateAnamnesisSecondaryDiagnosesNote({
    mutation: {
      onSuccess: (_, variables) =>
        queryClient.setQueryData<GetAnamnesesByPatient200>(
          getGetAnamnesesByPatientQueryKey(patientId),
          (oldAnamneses) =>
            oldAnamneses &&
            Object.fromEntries(
              Object.entries(oldAnamneses).map(([key, value]) => [
                key,
                value.id === anamnesisId ? { ...value, secondaryDiagnosesNote: variables.data.note } : value,
              ]),
            ),
        ),
      onError: () => toast.error('Beim Speichern der Bemerkungen ist ein Fehler aufgetreten'),
    },
  });

  const updateSecondaryDiagnosesMutation = useUpdateAnamnesisSecondaryDiagnoses({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetAnamnesesByPatientQueryKey(patientId) });
        setIsEditSecondaryDiagnosesDialogOpen(false);
      },
      onError: () => toast.error('Beim Bearbeiten der Nebendiagnosen ist ein Fehler aufgetreten'),
    },
  });

  const handleNoteBlur = () => {
    if (note === (secondaryDiagnosesNote ?? '')) {
      return;
    }

    updateSecondaryDiagnosesNoteMutation.mutate({
      id: anamnesisId,
      data: { note },
    });
  };

  return (
    <>
      <Card>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography level="title-lg">Nebendiagnosen</Typography>
          <Stack direction={'row'} gap={1}>
            <IconButton
              color={'primary'}
              disabled={!diagnoses.length}
              onClick={() => setIsEditSecondaryDiagnosesDialogOpen(true)}
              title={'Nebendiagnosen bearbeiten'}>
              <Icons.Edit />
            </IconButton>
            <Button onClick={() => setIsAddSecondaryDiagnoseDialogOpen(true)} endDecorator={<Icons.Add />}>
              Nebendiagnose hinzufügen
            </Button>
          </Stack>
        </Stack>
        <Divider />

        {diagnoses.length ? (
          <Sheet
            sx={
              shouldScrollDiagnoses
                ? { maxHeight: 'calc(var(--TableCell-height, 40px) * 11)', overflow: 'auto' }
                : undefined
            }>
            <Table stickyHeader={shouldScrollDiagnoses}>
              <thead>
                <tr>
                  <th style={{ width: 160 }}>Seit</th>
                  <th>Nebendiagnose</th>
                </tr>
              </thead>
              <tbody>
                {diagnoses.map((diagnosis) => (
                  <tr key={diagnosis.id}>
                    <td>
                      <TruncateText>{formatDateWithGermanMonthOrText(diagnosis.started)}</TruncateText>
                    </td>
                    <td>{getSecondaryDiagnosisTypeName(diagnosis.diagnosisType)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Sheet>
        ) : (
          <Stack alignItems={'center'} gap={1} p={3}>
            <Icons.Description />
            <Typography level="body-sm">Noch keine Nebendiagnosen vorhanden.</Typography>
          </Stack>
        )}
        <Divider />
        <Table>
          <tbody>
            <tr>
              <td style={{ paddingTop: 16, verticalAlign: 'top', width: 110 }}>
                <Badge
                  variant="solid"
                  badgeInset="50% 8px"
                  invisible={!isNoteDirty && !updateSecondaryDiagnosesNoteMutation.isPending}
                  color="warning"
                  sx={{ display: 'block' }}
                  slotProps={{ badge: { sx: { boxShadow: 'none' } } }}>
                  <Typography level="title-md">Bemerkungen</Typography>
                </Badge>
              </td>
              <td style={{ paddingTop: 16 }}>
                <FormControl>
                  <Textarea
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    onBlur={handleNoteBlur}
                    minRows={3}
                    maxRows={6}
                    variant="plain"
                  />
                </FormControl>
              </td>
            </tr>
          </tbody>
        </Table>
      </Card>
      <AddSecondaryDiagnoseDialog
        open={isAddSecondaryDiagnoseDialogOpen}
        onClose={() => setIsAddSecondaryDiagnoseDialogOpen(false)}
        anamnesisId={anamnesisId}
        patientId={patientId}
      />
      <EditAnamnesisItemsDialog
        open={isEditSecondaryDiagnosesDialogOpen}
        title={'Nebendiagnosen bearbeiten'}
        typeLabel={'Nebendiagnose'}
        emptyText={'Keine Nebendiagnosen vorhanden.'}
        items={diagnoses.map((diagnosis) => ({
          id: diagnosis.id,
          started: diagnosis.started,
          type: diagnosis.diagnosisType,
        }))}
        typeOptions={Object.values(SecondaryDiagnosisDtoDiagnosisType).map((type) => ({
          label: getSecondaryDiagnosisTypeName(type),
          value: type,
        }))}
        isLoading={updateSecondaryDiagnosesMutation.isPending}
        onClose={() => setIsEditSecondaryDiagnosesDialogOpen(false)}
        onSubmit={(items) =>
          updateSecondaryDiagnosesMutation.mutate({
            id: anamnesisId,
            data: items.map((item) => ({
              id: item.id,
              started: item.started,
              diagnosisType: item.type,
            })),
          })
        }
      />
    </>
  );
}
